import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useBuildingsWhere, useCanChangeRow, useCanOrganizationWide } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { DataTable } from "@/components/app/patterns/data-table";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { QbMappingForm, type QbMappingFormValues } from "./-components/billing/qb-mapping-form";
import { NeedsActivation } from "@/components/app/activation/needs-activation";
import { RowActions } from "@/components/app/patterns/row-actions";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";

export const Route = createFileRoute("/_authenticated/_administration/settings/qb-mappings")({
  beforeLoad: ({ context }) => guardRoute(context.me, "qb-mappings"),
  component: QbMappingsSettings,
});

type QbMapping = Output<typeof orpcClient.financials.qbMappings.list>[number];

const mappingColumns = (facilityName: (id: string | null) => string): ColumnDef<QbMapping>[] => [
  {
    id: "account",
    header: "QuickBooks account",
    meta: { className: "font-mono text-xs" },
    cell: ({ row: { original: mapping } }) => <OverflowText>{mapping.qbCategoryName}</OverflowText>,
  },
  {
    id: "category",
    header: "Maps to",
    cell: ({ row: { original: mapping } }) => (
      <OverflowText>{mapping.categoryName ?? "Unknown category"}</OverflowText>
    ),
  },
  {
    id: "facility",
    header: "Facility",
    cell: ({ row: { original: mapping } }) => (
      <OverflowText>{facilityName(mapping.facilityId)}</OverflowText>
    ),
  },
];

const fail = (error: Error) => toast.error(errorWords(error));

/** Which QuickBooks account an imported transaction lands in on this side of the wall. */
function QbMappingsSettings() {
  const { facilityId } = useFacility();
  // A mapping for every facility is the organization's bookkeepers' to add or remove.
  const everyFacility = useCanOrganizationWide("financial:manage");
  const canChange = useCanChangeRow("financial:manage");
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<QbMapping | null>(null);

  const mappings = useQuery(
    orpc.financials.qbMappings.list.queryOptions({ input: { facilityId } }),
  );
  const categories = useQuery(orpc.financials.expenseCategories.list.queryOptions({}));
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.financials.qbMappings.key() });

  const upsert = useMutation(
    orpc.financials.qbMappings.upsert.mutationOptions({
      onSuccess: async () => {
        toast.success("Mapping saved");
        setAdding(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.financials.qbMappings.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Mapping removed");
        setRemoving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const facilityRows = facilities.data ?? [];
  const keptHere = useBuildingsWhere("financial:manage", facilityRows);
  const canManage = everyFacility || keptHere.length > 0;
  const inView = keptHere.find((facility) => facility.id === facilityId)?.id;
  const mapsAt = inView ?? (everyFacility ? null : (keptHere[0]?.id ?? null));
  const facilityName = (id: string | null) =>
    id ? (facilityRows.find((facility) => facility.id === id)?.name ?? id) : "Every facility";
  const activeCategories = (categories.data ?? []).filter((category) => category.isActive);
  const rows = (mappings.data ?? []).toSorted((a, b) =>
    a.qbCategoryName.localeCompare(b.qbCategoryName),
  );

  const submit = (values: QbMappingFormValues) => upsert.mutate(values);

  return (
    <>
      <PageHeader
        title="QB account mappings"
        meta="Which expense category each QuickBooks account's transactions are booked to when they are imported."
      />

      <RecordCard
        title="Mappings"
        count={mappings.data ? rows.length : undefined}
        canWrite={canManage}
        addLabel="Add mapping"
        onAdd={() => setAdding(true)}
      >
        {mappings.isPending ? (
          <RowsSkeleton />
        ) : mappings.isError ? (
          <ReadFailed
            message="The mappings could not be loaded."
            error={mappings.error}
            retrying={mappings.isFetching}
            onRetry={() => void mappings.refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="flex flex-col gap-4">
            <NeedsActivation
              featureKey="quickbooks"
              facilityId={facilityId === "all" ? null : facilityId}
            />
            <EmptyRows text="No mappings yet. Until one exists, an import books nothing and lists each QuickBooks category it could not place." />
          </div>
        ) : (
          <DataTable
            flush
            quiet
            data={rows}
            columns={mappingColumns(facilityName)}
            noun="mappings"
            rowId={(mapping) => mapping.id}
            renderRowActions={
              canManage
                ? (mapping) => (
                    <>
                      {mapping.facilityId || everyFacility ? null : <SharedRowNote />}
                      <RowActions
                        label={mapping.qbCategoryName}
                        modal={false}
                        items={[
                          canChange(mapping.facilityId) && {
                            label: "Remove mapping",
                            destructive: true,
                            onSelect: () => setRemoving(mapping),
                          },
                        ]}
                      />
                    </>
                  )
                : undefined
            }
          />
        )}
      </RecordCard>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add mapping</DialogTitle>
            <DialogDescription>
              Saving over an account name that is already mapped for the same facility replaces that
              mapping rather than adding a second one.
            </DialogDescription>
          </DialogHeader>
          {adding ? (
            <QbMappingForm
              facilities={keptHere}
              everyFacility={everyFacility}
              defaultFacilityId={mapsAt}
              categories={activeCategories}
              pending={upsert.isPending}
              onSubmit={submit}
              onCancel={() => setAdding(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={removing !== null}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove the mapping for &ldquo;{removing?.qbCategoryName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Future transactions from{" "}
              <span className="font-mono text-xs">{removing?.qbCategoryName}</span> are skipped and
              listed as unplaced until a new mapping is added. Entries already imported keep the
              category they were given.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing && remove.mutate({ mappingId: removing.id })}
            >
              Remove mapping
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
